import { useEffect, useState } from "react";
import { useCauseLens, useRunCauseLens, type CauseLensAnswer } from "@/api/queries";
import { artifactCsvUrl } from "@/api/queries";
import { fromLive, type TraceRow } from "@/chat/trace";
import { useRunStream } from "@/chat/useRunStream";
import type { Artifact } from "@/artifacts/types";
import { ArtifactCard } from "@/components/artifacts/ArtifactCard";
import { Markdown } from "@/components/chat/Markdown";
import { TraceTimeline } from "@/components/chat/TraceTimeline";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { EmptyState } from "@/components/ui/EmptyState";
import { AlertIcon, SparkIcon } from "@/components/icons";
import { useRelativeTime } from "@/lib/time";

/**
 * Cause Lens (PRD FR-7.6): "what is driving this change?"
 *
 * The run streams over the same SSE hook a chat turn uses, and its trace
 * renders through the same timeline — because a Cause Lens run **is** an agent
 * run, in a thread of its own. It owes the same evidence any other answer
 * owes: every number in it came from a statement that is one click away.
 *
 * The panel is explicit about which comparison it made. With two snapshots the
 * movement is measured; with fewer, the model had to derive the prior period
 * from the report's own window, and the badge says `inferred` so nobody reads
 * a derived baseline as a captured one.
 */

function storedRows(answer: CauseLensAnswer): TraceRow[] {
  return answer.steps.map((step) => ({
    seq: Number(step.seq ?? 0),
    tool: String(step.tool ?? ""),
    sql: String(step.sql ?? ""),
    rowCount: typeof step.row_count === "number" ? step.row_count : null,
    durationMs: typeof step.duration_ms === "number" ? step.duration_ms : null,
    truncated: false,
    outcome:
      step.outcome === "ok" ? "ok" : step.outcome === "blocked" ? "blocked" : "error",
    error: (step.error_message as string | null) ?? null,
    queryIndex: null,
    retryOf: null,
    relations: (step.relations as string[] | undefined) ?? [],
    arguments: {},
    // Cause Lens runs on one source (a named multi-source follow-up).
    connectionId: null,
    source: null,
    inputs: [],
    inputsTruncated: [],
  }));
}

export function CauseLensPanel({
  reportId,
  connectionId,
  canRun,
  attach,
}: {
  reportId: string;
  connectionId: string | null;
  canRun: boolean;
  /** A run started elsewhere — "explain this breach" (ALERT-3) — to stream here. */
  attach?: { runId: string; threadId: string; basis: string } | null;
}) {
  const { data: answer, isLoading, refetch } = useCauseLens(reportId);
  const start = useRunCauseLens(reportId);
  const [runId, setRunId] = useState<string | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [basis, setBasis] = useState<string | null>(null);
  const { run } = useRunStream(runId, threadId);
  const stamp = useRelativeTime(answer?.created_at);

  const streaming = run.status === "connecting" || run.status === "running";

  useEffect(() => {
    if (!attach) return;
    setBasis(attach.basis);
    setThreadId(attach.threadId);
    setRunId(attach.runId);
  }, [attach]);

  async function ask() {
    // The failure is announced by the mutation cache (backlog item 11);
    // this only has to avoid attaching to a run that never started.
    const started = await start.mutateAsync({}).catch(() => null);
    if (!started) return;
    setBasis(started.basis);
    setThreadId(started.thread_id);
    setRunId(started.run_id);
  }

  // The stored answer arrives once the run finishes; refetching here is what
  // turns the live stream into the persisted trace without a page reload.
  // In an effect rather than during render: a run that ends is an event, and
  // setting state on the way past would re-render forever.
  useEffect(() => {
    if (!runId) return;
    if (run.status !== "complete" && run.status !== "error") return;
    setRunId(null);
    void refetch();
  }, [run.status, runId, refetch]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          icon={<SparkIcon className="size-4" />}
          loading={start.isPending || streaming}
          disabled={!canRun}
          onClick={() => void ask()}
        >
          {answer ? "Ask again" : "What's driving this?"}
        </Button>
        {basis ? (
          <Chip tone={basis === "snapshots" ? "neutral" : "warning"}>
            {basis === "snapshots" ? "comparing two snapshots" : "no earlier snapshot — inferred"}
          </Chip>
        ) : null}
        {answer && !streaming ? (
          <span className="type-caption text-ink-disabled normal-case">
            asked {stamp}
            {answer.model_label ? ` · ${answer.model_label}` : ""}
          </span>
        ) : null}
      </div>

      {!canRun ? (
        <p className="type-support text-ink-secondary flex items-start gap-2">
          <AlertIcon className="text-warning mt-0.5 size-3.5 shrink-0" />
          <span>
            This report's data source no longer exists, so there is nothing to query for a driver
            analysis.
          </span>
        </p>
      ) : null}

      {streaming ? (
        <div className="flex flex-col gap-3">
          <p className="type-support text-ink-secondary">{run.phase || "Thinking…"}</p>
          <TraceTimeline
            rows={run.steps.map(fromLive)}
            connectionId={connectionId}
            defaultOpen
            live
          />
          {run.text ? (
            <div className="type-body text-ink">
              <Markdown>{run.text}</Markdown>
            </div>
          ) : null}
        </div>
      ) : null}

      {run.error ? (
        <p className="bg-error-subtle text-error type-support rounded-element px-3 py-2">
          {run.error.message}
        </p>
      ) : null}

      {!streaming && answer ? (
        <div className="flex flex-col gap-4">
          {answer.text ? (
            <div className="type-body text-ink max-w-3xl">
              <Markdown>{answer.text}</Markdown>
            </div>
          ) : null}

          {answer.insights.length ? (
            <ul className="flex max-w-3xl flex-col gap-1.5">
              {answer.insights.map((insight) => (
                <li key={insight} className="type-support text-ink-secondary flex gap-2">
                  <SparkIcon className="text-accent mt-0.5 size-3.5 shrink-0" />
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {answer.warnings.map((warning) => (
            <p
              key={warning}
              className="bg-warning-subtle text-warning type-support rounded-element flex max-w-3xl items-start gap-2 px-3 py-2"
            >
              <AlertIcon className="mt-0.5 size-3.5 shrink-0" />
              <span>{warning}</span>
            </p>
          ))}

          <div className="grid gap-4 lg:grid-cols-2">
            {(answer.artifacts as Artifact[]).map((artifact, index) => (
              <ArtifactCard
                key={`${answer.message_id}-${index}`}
                artifact={artifact}
                csvUrl={artifactCsvUrl(answer.message_id, index)}
                className={artifact.type === "table" ? "lg:col-span-2" : undefined}
              />
            ))}
          </div>

          <TraceTimeline rows={storedRows(answer)} connectionId={connectionId} />

          {answer.sources.length ? (
            <p className="type-caption text-ink-disabled normal-case">
              Sources: {answer.sources.join(" · ")}
            </p>
          ) : null}
        </div>
      ) : null}

      {!streaming && !answer && !isLoading ? (
        <EmptyState
          icon={<SparkIcon className="size-6" />}
          title="Nothing asked yet"
          detail={
            "Cause Lens compares this report's latest snapshot with the one before it and works " +
            "out which parts of the business moved — current, previous, the change, and each " +
            "driver's share of the total change."
          }
        />
      ) : null}
    </div>
  );
}
